import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { GlobalOutlookChapter1 } from '@/components/stories/global-outlook/chapter-1';
import { GlobalOutlookChapter2 } from '@/components/stories/global-outlook/chapter-2';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/global-outlook.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as GlobalOutlookData }),
  getStoryProps('global-outlook', 'future-fortunes')
);

export default function GlobalOutlook(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('global-outlook');

  const pageTitle = t({ id: 'global-outlook.title', message: 'Global outlook' });
  const pageDescription = t({
    id: 'global-outlook.description',
    message: 'Are our best days behind us or still to come? See how your take compares.',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.sky} hasBackButton>
        <StoryHeader
          backgroundId={storySlug}
          css={{
            '@bp9': {
              backgroundPosition: 'left center',
            },
          }}
        >
          <StoryTheme>
            <Trans id="global-outlook.theme">Optimism</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="global-outlook.tagline">
              <Text type="heading">
                Are our <span>best days</span> behind us or still to come? See how your take
                compares with people around the world.
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <GlobalOutlookChapter1 data={data} />
        <GlobalOutlookChapter2 data={data} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@media screen and (min-aspect-ratio: 1250/900)': {
              marginBlockStart: 'calc(50vh + 25vw)',
            },
          }}
          relatedStoryId={relatedStoryId}
          sharing={{
            title: t({ id: 'global-outlook.sharing.title', message: 'Global outlook' }),
            text: t({
              id: 'global-outlook.sharing.text',
              message: 'Are our best days behind us or still to come? See how your take compares.',
            }),
            url: sharingUrl,
          }}
        >
          <Trans id="global-outlook.closing-question">
            Why do you think young people are more <AnnotatedType>optimistic</AnnotatedType> about
            the world's trajectory?
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
